import { Graphics, Text, type Container, type Sprite } from 'pixi.js';
import { GlitchFilter, GlowFilter } from 'pixi-filters';
import { inLight, raycastHit } from '../lib/geometry.js';
import { findTakedownTarget } from '../systems/abilities.js';
import { cameraDir, findHackTarget } from '../systems/cameras.js';
import { objectiveProgress } from '../systems/objective.js';
import type { Camera, Guard, Particle, World } from '../world/types.js';
import { tex } from './assets.js';
import type { AssetKey } from './assets.js';
import {
  cameraSprites,
  getExitPadSprite,
  getPlayerSprite,
  guardBodySprites,
  guardSprites,
  intelSprites,
} from './static.js';
import type { Stage } from './stage.js';

// One persistent Graphics per dynamic layer — cleared and rebuilt each frame.
interface DynamicGraphics {
  exitPad: Graphics;
  sounds: Graphics;
  decoys: Graphics;
  vision: Graphics;
  cameras: Graphics;
  boxes: Graphics;
  bodies: Graphics;
  player: Graphics;
  guards: Graphics;
  particles: Graphics;
}

interface AlertText {
  text: Text;
  ownerId: number;
}

let dyn: DynamicGraphics | null = null;
let alertPool: AlertText[] = [];
// GlitchFilter is shared across all camera Graphics — we add it to dyn.cameras.filters
// when at least one camera is currently hacked, and remove otherwise.
let cameraGlitchFilter: GlitchFilter | null = null;

export function initRenderer(stage: Stage): void {
  dyn = {
    exitPad: ensureSingleGraphics(stage.layers.exitPad),
    sounds: ensureSingleGraphics(stage.layers.sounds),
    decoys: ensureSingleGraphics(stage.layers.decoys),
    vision: ensureSingleGraphics(stage.layers.vision),
    cameras: ensureSingleGraphics(stage.layers.cameras),
    boxes: ensureSingleGraphics(stage.layers.boxes),
    bodies: ensureSingleGraphics(stage.layers.bodies),
    player: ensureSingleGraphics(stage.layers.player),
    guards: ensureSingleGraphics(stage.layers.guards),
    particles: ensureSingleGraphics(stage.layers.particles),
  };

  // Reusable glitch filter applied to dyn.cameras whenever any camera is
  // currently hacked. We bump the seed each frame for animated noise.
  cameraGlitchFilter = new GlitchFilter({
    slices: 6,
    offset: 4,
    fillMode: 0, // TRANSPARENT
    red: { x: 2, y: 0 } as { x: number; y: number },
    green: { x: 0, y: 0 } as { x: number; y: number },
    blue: { x: -2, y: 0 } as { x: number; y: number },
  });
  dyn.cameras.filters = [];
  // Additive blend on intentionally-glowy layers. Stacks brightness
  // into a believable bloom on top of the dark world background.
  dyn.exitPad.blendMode = 'add';
  dyn.particles.blendMode = 'add';
  dyn.sounds.blendMode = 'add';
  dyn.decoys.blendMode = 'add';

  // Mint rim-light around the player silhouette. Sits on the sprite
  // directly so the glow tracks the PNG's transparent edges. The dyn
  // Graphics (floor shadow) stays unfiltered to keep the shadow black.
  // Strong outer strength + a hue distinct from the guards' red rim so
  // the player reads instantly even on a busy floor.
  const playerSprite = getPlayerSprite();
  if (playerSprite) {
    playerSprite.filters = [
      new GlowFilter({
        color: 0x4afca0,
        distance: 12,
        outerStrength: 2.4,
        innerStrength: 0,
        quality: 0.4,
      }),
    ];
    // The static Sprite was added to the player layer before
    // ensureSingleGraphics created the dyn Graphics, so right now the
    // sprite renders UNDER the shadow. Re-add it to push it back to
    // the top of the children list.
    stage.layers.player.addChild(playerSprite);
  }

  // Subtle red rim on each guard sprite — helps them stand out against
  // floor patterns. Sits on the sprite directly so the glow tracks the
  // PNG silhouette; dyn.guards stays unfiltered so the floor shadow
  // and alert rings render with their own colours.
  for (const sprite of guardSprites.values()) {
    sprite.filters = [
      new GlowFilter({
        color: 0xff5060,
        distance: 8,
        outerStrength: 0.9,
        innerStrength: 0,
        quality: 0.3,
      }),
    ];
    // The static sprite was added before ensureSingleGraphics created
    // the dyn Graphics, so it's currently underneath. Re-adding it
    // moves it back to the top of the children list — shadow + alert
    // ring drawn in dyn render UNDER the sprite.
    stage.layers.guards.addChild(sprite);
  }

  // Same re-stack for KO body sprites — without this, the floor shadow
  // ellipse drawn in dyn.bodies covers the body PNG instead of sitting
  // beneath it.
  for (const body of guardBodySprites.values()) {
    stage.layers.bodies.addChild(body);
  }

  alertPool = [];
  stage.layers.alerts.removeChildren();
}

/**
 * Find or create the dynamic Graphics object inside a layer. Uses the
 * `__dyn` label to distinguish it from any static Sprites the asset
 * pipeline may have parked in the same layer (exit pad sprite, intel
 * sprites, etc.). Reusing the existing Graphics across calls means
 * the renderer can be re-initialised on level change without wiping
 * static children.
 */
function ensureSingleGraphics(layer: Container): Graphics {
  for (const child of layer.children) {
    if (child.label === '__dyn' && child instanceof Graphics) {
      child.clear();
      return child;
    }
  }
  const g = new Graphics();
  g.label = '__dyn';
  layer.addChild(g);
  return g;
}

/**
 * Paint the player's visible region into the given Graphics. White
 * (opaque) inside the region, empty outside. Composed of an
 * omnidirectional disc plus a forward cone; both raycast-clipped so
 * walls block sight. Used as a Pixi mask for fog-of-war levels.
 */
export function drawVisibilityMask(g: Graphics, world: World): void {
  g.clear();
  const p = world.player;

  // Omnidirectional disc — what you can sense around you in the dark.
  const OMNI_R = 110;
  const OMNI_RAYS = 36;
  const omni: number[] = [];
  for (let i = 0; i < OMNI_RAYS; i++) {
    const a = (Math.PI * 2 * i) / OMNI_RAYS;
    const tx = p.x + Math.cos(a) * OMNI_R;
    const ty = p.y + Math.sin(a) * OMNI_R;
    const hit = raycastHit(world, p.x, p.y, tx, ty);
    if (hit) omni.push(hit.x, hit.y);
    else omni.push(tx, ty);
  }
  g.poly(omni).fill(0xffffff);

  // Forward cone — what your facing eyes pick up further away.
  const CONE_R = 240;
  const CONE_FOV = Math.PI * 0.7; // ~126°
  const CRAYS = 30;
  const cone: number[] = [p.x, p.y];
  for (let i = 0; i <= CRAYS; i++) {
    const a = p.dir - CONE_FOV / 2 + (CONE_FOV * i) / CRAYS;
    const tx = p.x + Math.cos(a) * CONE_R;
    const ty = p.y + Math.sin(a) * CONE_R;
    const hit = raycastHit(world, p.x, p.y, tx, ty);
    if (hit) cone.push(hit.x, hit.y);
    else cone.push(tx, ty);
  }
  g.poly(cone).fill(0xffffff);

  // Light spills — every lamp illuminates its own disc, raycast-clipped
  // by walls (so a corridor full of light still gets revealed even if
  // the player is across the map). Mirrors the shadow/visibility logic
  // in lib/geometry.inLight, just on the rendering side: anything lit
  // is shown, anything dark requires the player's own senses.
  const LIGHT_RAYS = 28;
  for (const L of world.lights) {
    const lit: number[] = [];
    for (let i = 0; i < LIGHT_RAYS; i++) {
      const a = (Math.PI * 2 * i) / LIGHT_RAYS;
      const tx = L.x + Math.cos(a) * L.r;
      const ty = L.y + Math.sin(a) * L.r;
      const hit = raycastHit(world, L.x, L.y, tx, ty);
      if (hit) lit.push(hit.x, hit.y);
      else lit.push(tx, ty);
    }
    g.poly(lit).fill(0xffffff);
  }
}

export function renderFrame(stage: Stage, world: World): void {
  if (!dyn) return;
  drawExitPad(dyn.exitPad, world);
  drawSounds(dyn.sounds, world);
  drawDecoys(dyn.decoys, world);
  drawVisionCones(dyn.vision, world);
  drawCamerasLayer(dyn.cameras, world);
  drawBoxOnFloor(dyn.boxes, world);
  drawBodies(dyn.bodies, world);
  drawPlayerOrCarriedBox(dyn.player, world);
  drawGuards(dyn.guards, world);
  drawParticles(dyn.particles, world);
  drawAlerts(stage, world);
}

// ===== Geometry helpers =====

/** Rotate a local point around (cx, cy) by `angle`. Used by the
 * spinning decoy cube — player and guard silhouettes are now sprites
 * so they don't need rotated polygon helpers anymore. */
function rot(cx: number, cy: number, lx: number, ly: number, angle: number): [number, number] {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return [cx + lx * cos - ly * sin, cy + lx * sin + ly * cos];
}

// ===== Layer drawers =====

function drawExitPad(g: Graphics, world: World): void {
  g.clear();
  drawCollectibles(g, world);

  const e = world.exitPos;
  const t = performance.now() / 280;
  const pulse = 0.5 + 0.5 * Math.sin(t);

  // Pad colour shifts to amber when the objective isn't complete yet.
  const prog = objectiveProgress(world);
  const accent = prog.extractLocked ? 0xffb84a : 0x4afca0;

  // Tint the static sprite so the body recolours with state, breathing
  // gently in sync with the halo rings drawn on top.
  const padSprite = getExitPadSprite();
  if (padSprite) {
    padSprite.tint = accent;
    padSprite.alpha = 0.85 + 0.15 * pulse;
  }

  // Outer halo glow — soft pulsing rings around the sprite.
  for (let i = 0; i < 3; i++) {
    const r = 22 + i * 6 + pulse * 3;
    g.circle(e.x, e.y, r).stroke({ color: accent, alpha: 0.18 - i * 0.05, width: 1.5 });
  }

  // Padlock symbol when extraction is locked.
  if (prog.extractLocked) {
    g.rect(e.x - 4, e.y - 1, 8, 7).fill({ color: 0xffb84a, alpha: 0.55 });
    g.circle(e.x, e.y - 2, 3).stroke({ color: 0xffb84a, alpha: 0.8, width: 1.5 });
  }
}

function drawCollectibles(g: Graphics, world: World): void {
  const t = performance.now() / 1000;
  for (const c of world.collectibles) {
    const sprite = intelSprites.get(c);
    if (c.picked) {
      if (sprite) sprite.visible = false;
      // Pickup animation — expanding ring that fades.
      if (c.pickupT > 0) {
        const r = (0.6 - c.pickupT) * 30 + 8;
        g.circle(c.x, c.y, r).stroke({
          color: 0xffe066,
          alpha: c.pickupT,
          width: 2,
        });
      }
      continue;
    }
    // Subtle breathing alpha on the static sprite — keeps the icon alive
    // without redrawing geometry every frame.
    const pulse = 0.7 + 0.3 * Math.sin(t * 4);
    if (sprite) {
      sprite.visible = true;
      sprite.alpha = 0.85 + 0.15 * pulse;
    }
    // Soft halo behind the sprite — readability cue against the floor.
    g.circle(c.x, c.y, 12 + pulse * 1.5).fill({ color: 0xffe066, alpha: 0.14 });
  }
}

function drawSounds(g: Graphics, world: World): void {
  g.clear();
  for (const s of world.sounds) {
    const t = 1 - s.life / s.maxLife;
    const r = s.radius * t;
    const { color, alpha } = parseRgba(s.color);
    g.circle(s.x, s.y, r).stroke({ color, alpha, width: 2 * (1 - t) + 1 });
    // Inner ghost ring slightly behind for a chromatic-aberration vibe.
    if (r > 8) {
      g.circle(s.x, s.y, r * 0.85).stroke({ color, alpha: alpha * 0.4, width: 1 });
    }
  }
}

function drawDecoys(g: Graphics, world: World): void {
  g.clear();
  const now = performance.now();
  for (const d of world.decoys) {
    if (!d.landed) {
      // In flight: bright comet-like dot
      g.circle(d.x, d.y, 5).fill({ color: 0xa0e8ff, alpha: 0.4 });
      g.circle(d.x, d.y, 3).fill(0xffffff);
    } else {
      // Spinning emitter cube
      const ang = now / 220;
      const half = 5;
      const corners = [
        [-half, -half],
        [half, -half],
        [half, half],
        [-half, half],
      ] as Array<[number, number]>;
      const pts: number[] = [];
      for (const [lx, ly] of corners) {
        const [px, py] = rot(d.x, d.y, lx, ly, ang);
        pts.push(px, py);
      }
      g.poly(pts).fill(0x77aaff);
      g.poly(pts).stroke({ color: 0xcceeff, width: 1 });
      // Pulsing inner glow
      const glow = 0.5 + 0.5 * Math.sin(now / 120);
      g.circle(d.x, d.y, 2 + glow * 1.5).fill({ color: 0xffffff, alpha: 0.6 });
    }
  }
}

function drawVisionCones(g: Graphics, world: World): void {
  g.clear();
  const RAYS = 30;
  const t = performance.now() / 1000;
  for (const guard of world.guards) {
    if (guard.ko) continue;
    const points: number[] = [guard.x, guard.y];
    let maxR = 0;
    for (let i = 0; i <= RAYS; i++) {
      const a = guard.dir - guard.fov / 2 + (guard.fov * i) / RAYS;
      const tx = guard.x + Math.cos(a) * guard.vision;
      const ty = guard.y + Math.sin(a) * guard.vision;
      const hit = raycastHit(world, guard.x, guard.y, tx, ty);
      if (hit) {
        points.push(hit.x, hit.y);
        const d = Math.hypot(hit.x - guard.x, hit.y - guard.y);
        if (d > maxR) maxR = d;
      } else {
        points.push(tx, ty);
        if (guard.vision > maxR) maxR = guard.vision;
      }
    }
    const colors = pickConeColors(guard, t);
    // Two-pass cone gradient — kept tame so the floor stays readable
    // beneath the cone. The outline + animated scanlines below carry
    // most of the visual weight; the fill is just a hue cue.
    g.poly(points).fill({ color: colors.fillColor, alpha: colors.fillAlpha * 0.35 });
    const innerPoints: number[] = [guard.x, guard.y];
    for (let i = 2; i < points.length; i += 2) {
      innerPoints.push(guard.x + (points[i]! - guard.x) * 0.5);
      innerPoints.push(guard.y + (points[i + 1]! - guard.y) * 0.5);
    }
    g.poly(innerPoints).fill({ color: colors.fillColor, alpha: colors.fillAlpha * 0.6 });
    // Outline only on the outer cone — keeps the silhouette readable.
    g.poly(points).stroke({ color: colors.strokeColor, alpha: colors.strokeAlpha, width: 1 });

    // Animated radial scanlines — concentric arcs sweeping outward.
    const sweep = ((t * 0.25) % 1) * maxR;
    const arcs = 3;
    for (let i = 0; i < arcs; i++) {
      const r = sweep + (i * maxR) / arcs;
      if (r < 4 || r > maxR) continue;
      const ringPts: number[] = [];
      const arcSegs = 16;
      for (let k = 0; k <= arcSegs; k++) {
        const a = guard.dir - guard.fov / 2 + (guard.fov * k) / arcSegs;
        ringPts.push(guard.x + Math.cos(a) * r, guard.y + Math.sin(a) * r);
      }
      // Don't fill — just a thin stroke that fades with distance
      const fade = 1 - r / maxR;
      g.poly(ringPts).stroke({
        color: colors.strokeColor,
        alpha: colors.strokeAlpha * 0.45 * fade,
        width: 1,
      });
    }
  }
}

// ===== Cameras =====

function drawCamerasLayer(g: Graphics, world: World): void {
  g.clear();
  drawCameras(g, world);
  // Toggle the glitch filter on/off based on whether any camera is
  // currently being hacked. Animate the seed for moving noise.
  if (!cameraGlitchFilter) return;
  const anyHacked = world.cameras.some((c) => c.hackT > 0);
  if (anyHacked) {
    if (!g.filters || (g.filters as unknown as GlitchFilter[]).length === 0) {
      g.filters = [cameraGlitchFilter];
    }
    cameraGlitchFilter.seed = Math.random();
    cameraGlitchFilter.refresh();
  } else if (g.filters && (g.filters as unknown as GlitchFilter[]).length > 0) {
    g.filters = [];
  }
}

function drawCameras(g: Graphics, world: World): void {
  const t = performance.now() / 1000;
  // Highlight the camera that would be hacked if the player presses H now.
  const hackable = findHackTarget(world);
  for (const cam of world.cameras) {
    drawCameraCone(g, world, cam, t);
    drawCameraBody(g, cam, t, cam === hackable);
  }
}

function drawCameraCone(g: Graphics, world: World, cam: Camera, t: number): void {
  if (cam.hackT > 0) return; // hacked = no cone
  const dir = cameraDir(cam);
  const RAYS = 22;
  const points: number[] = [cam.x, cam.y];
  for (let i = 0; i <= RAYS; i++) {
    const a = dir - cam.fov / 2 + (cam.fov * i) / RAYS;
    const tx = cam.x + Math.cos(a) * cam.vision;
    const ty = cam.y + Math.sin(a) * cam.vision;
    const hit = raycastHit(world, cam.x, cam.y, tx, ty);
    if (hit) points.push(hit.x, hit.y);
    else points.push(tx, ty);
  }
  const pulse = 0.85 + 0.15 * Math.sin(t * 5);
  g.poly(points).fill({ color: 0xff4060, alpha: 0.18 * pulse });
  g.poly(points).stroke({ color: 0xff7090, alpha: 0.55, width: 1 });
}

function drawCameraBody(g: Graphics, cam: Camera, t: number, hackable: boolean): void {
  const hacked = cam.hackT > 0;
  const dir = cameraDir(cam);

  // Sync the static dome sprite so it points where the camera looks.
  // Sprite art has its lens facing right (+X), so rotation = dir maps
  // 1:1. Tint + alpha give a "powered down" look while hacked.
  const sprite = cameraSprites.get(cam);
  if (sprite) {
    sprite.rotation = dir;
    sprite.alpha = hacked ? 0.55 : 1.0;
    sprite.tint = hacked ? 0x6a7080 : 0xffffff;
  }

  if (hacked) {
    // Cyan glitch crosses + countdown ring overlaid on the dimmed sprite.
    g.moveTo(cam.x - 7, cam.y - 7)
      .lineTo(cam.x + 7, cam.y + 7)
      .moveTo(cam.x + 7, cam.y - 7)
      .lineTo(cam.x - 7, cam.y + 7)
      .stroke({ color: 0x4afca0, alpha: 0.85, width: 2 });
    // Hack countdown ring
    const frac = cam.hackT / 6; // mirrors HACK_DURATION = 6
    const arcSegs = 24;
    const ring: number[] = [];
    for (let i = 0; i <= Math.floor(arcSegs * frac); i++) {
      const a = -Math.PI / 2 + (Math.PI * 2 * i) / arcSegs;
      ring.push(cam.x + Math.cos(a) * 13, cam.y + Math.sin(a) * 13);
    }
    if (ring.length >= 4) {
      g.poly(ring).stroke({ color: 0x4afca0, alpha: 0.55, width: 1.5 });
    }
  }

  // "Hack me" hint — pulsing cyan ring when in range and not hacked
  if (hackable && !hacked) {
    const pulse = 0.5 + 0.5 * Math.sin(t * 8);
    g.circle(cam.x, cam.y, 14 + pulse * 1.5).stroke({
      color: 0x4afca0,
      alpha: 0.55 + 0.3 * pulse,
      width: 1.5,
    });
  }
}

interface ConeColors {
  fillColor: number;
  fillAlpha: number;
  strokeColor: number;
  strokeAlpha: number;
}

function pickConeColors(guard: Guard, t: number): ConeColors {
  const pulse = 0.7 + 0.3 * Math.sin(t * 6);
  if (guard.state === 'chase') {
    return {
      fillColor: 0xff4a5a,
      fillAlpha: 0.18 * pulse + 0.08,
      strokeColor: 0xff6a78,
      strokeAlpha: 0.75,
    };
  }
  if (guard.state === 'suspicious' || guard.state === 'investigate') {
    const aPulse = 0.85 + 0.15 * Math.sin(t * 4);
    return {
      fillColor: 0xffb84a,
      fillAlpha: 0.13 * aPulse + 0.04,
      strokeColor: 0xffd16a,
      strokeAlpha: 0.6,
    };
  }
  return {
    fillColor: 0xfffab4,
    fillAlpha: 0.07,
    strokeColor: 0xfffab4,
    strokeAlpha: 0.4,
  };
}

function drawBoxOnFloor(g: Graphics, world: World): void {
  g.clear();
  if (world.activeBox && !world.player.inBox) {
    drawBox(g, world.activeBox.x, world.activeBox.y, false, !!world.activeBox.body);
  }
}

function drawBodies(g: Graphics, world: World): void {
  g.clear();
  for (const guard of world.guards) {
    const body = guardBodySprites.get(guard);
    if (!body) continue;
    if (guard.ko && !guard.hiddenInBox) {
      drawBody(g, guard, body);
    } else {
      body.visible = false;
    }
  }
}

function drawPlayerOrCarriedBox(g: Graphics, world: World): void {
  g.clear();
  const sprite = getPlayerSprite();
  if (world.player.inBox && world.activeBox) {
    if (sprite) sprite.visible = false;
    drawBox(g, world.activeBox.x, world.activeBox.y, true, !!world.activeBox.body);
  } else {
    if (sprite) sprite.visible = true;
    drawPlayer(g, world);
  }
}

function drawGuards(g: Graphics, world: World): void {
  g.clear();
  // Pulsing green ring around any guard the player could currently take down.
  const opp = findTakedownTarget(world);
  if (opp) {
    const t = performance.now() / 250;
    const pulse = 0.55 + 0.45 * Math.sin(t);
    g.circle(opp.guard.x, opp.guard.y, 16).stroke({
      color: opp.chase ? 0xffd040 : 0x4afca0,
      alpha: 0.7 * pulse,
      width: 2,
    });
    // Soft halo
    g.circle(opp.guard.x, opp.guard.y, 20 + pulse * 2).stroke({
      color: 0x4afca0,
      alpha: 0.18 * pulse,
      width: 1,
    });
  }
  for (const guard of world.guards) {
    drawGuard(g, guard);
  }
}

/**
 * Map a guard facing angle (radians) to one of the directional texture
 * keys. Eight 45° sectors centred on the cardinals/ordinals.
 */
function guardDirToKey(dir: number): AssetKey {
  const d = Math.atan2(Math.sin(dir), Math.cos(dir));
  const slice = Math.PI / 8;
  if (d > -slice && d <= slice) return 'guard.e';
  if (d > slice && d <= 3 * slice) return 'guard.se';
  if (d > 3 * slice && d <= 5 * slice) return 'guard.s';
  if (d > 5 * slice && d <= 7 * slice) return 'guard.sw';
  if (d > 7 * slice || d <= -7 * slice) return 'guard.w';
  if (d > -7 * slice && d <= -5 * slice) return 'guard.nw';
  if (d > -5 * slice && d <= -3 * slice) return 'guard.n';
  return 'guard.ne';
}

// ===== Player (sprite-driven, with dynamic floor shadow) =====

/**
 * Map a facing angle to one of nine player textures across 8 cardinal
 * + ordinal directions. dir is in radians, +X right and +Y down
 * (Pixi convention), so dir = 0 → east, dir = π/2 → south, etc.
 *  - South (player facing the camera) is the most-seen pose; it has
 *    two walk frames `s_a` / `s_b` alternated via walkT.
 *  - All other directions are static — the bob in drawPlayer adds the
 *    sense of motion when moving.
 */
function playerDirToKey(dir: number, walkT: number): AssetKey {
  const d = Math.atan2(Math.sin(dir), Math.cos(dir));
  const slice = Math.PI / 8;
  if (d > -slice && d <= slice) return 'player.e';
  if (d > slice && d <= 3 * slice) return 'player.se';
  if (d > 3 * slice && d <= 5 * slice) {
    return Math.sin(walkT * 6) >= 0 ? 'player.s_a' : 'player.s_b';
  }
  if (d > 5 * slice && d <= 7 * slice) return 'player.sw';
  if (d > 7 * slice || d <= -7 * slice) return 'player.w';
  if (d > -7 * slice && d <= -5 * slice) return 'player.nw';
  if (d > -5 * slice && d <= -3 * slice) return 'player.n';
  return 'player.ne';
}

function drawPlayer(g: Graphics, world: World): void {
  const p = world.player;
  const speed = Math.hypot(p.vx, p.vy);
  const moving = speed > 12;

  // Walk-cycle phase (sin) drives bob; the texture-key helper picks
  // the right pose (animating only the south-facing one).
  const phase = moving ? Math.sin(p.walkT * 6) : 0;
  const bob = moving ? Math.abs(phase) * 1.2 : Math.sin(performance.now() / 700) * 0.4;

  // Floor shadow — drawn under the sprite. Stays put while the sprite
  // bobs slightly with the walk cycle.
  g.ellipse(p.x + 2, p.y + 8, 12, 4).fill({ color: 0x000000, alpha: 0.6 });

  const sprite = getPlayerSprite();
  if (!sprite) return;

  sprite.texture = tex(playerDirToKey(p.dir, p.walkT));
  // Uniform scale to keep silhouette height consistent across poses
  // with varying aspect ratios (sideways wider than front/back).
  const PLAYER_TARGET_H = 40;
  const s = PLAYER_TARGET_H / sprite.texture.height;
  sprite.scale.set(s, s);
  sprite.position.set(p.x, p.y - bob);

  // Stealth dim when in shadow — multiplicative tint preserves the
  // teal pad/visor accents while darkening the body. Visible alpha
  // also drops a touch to fade into the gloom.
  const visible = inLight(world, p);
  if (visible) {
    sprite.tint = 0xffffff;
    sprite.alpha = 1;
  } else {
    sprite.tint = 0x6088a8;
    sprite.alpha = 0.78;
  }
}

// ===== Guards (sprite-driven, with dynamic floor shadow + alert tint) =====

function drawGuard(g: Graphics, guard: Guard): void {
  const sprite = guardSprites.get(guard);
  if (!sprite) return;

  // KO guards are drawn separately by drawBodies (lying down). Hide
  // the standing sprite while in that state.
  if (guard.ko) {
    sprite.visible = false;
    return;
  }
  sprite.visible = true;

  // Use the actual per-tick speed populated by guards.ts. A state-
  // based check would wrongly freeze patrolling guards on the idle
  // texture even while they walk between waypoints.
  const moving = guard.currentSpeed > 8;
  const phase = Math.sin(guard.walkT * 6);
  const bob = moving ? Math.abs(phase) * 1.0 : Math.sin(performance.now() / 800) * 0.3;

  // Floor shadow under the sprite — drawn into the dyn Graphics, which
  // sits underneath all guard sprites in the layer (initRenderer
  // re-adds the sprites on top).
  g.ellipse(guard.x + 2, guard.y + 8, 12, 4).fill({ color: 0x000000, alpha: 0.6 });

  // Pick texture: idle when standing still, otherwise the matching
  // 8-direction pose. Source PNGs have varied aspect ratios per pose
  // (sideways are wider than front/back), so apply a uniform scale
  // based on the texture's height to keep the silhouette consistent
  // (target 44 px tall) and let width track the natural pose width.
  const key: AssetKey = moving ? guardDirToKey(guard.dir) : 'guard.idle';
  sprite.texture = tex(key);
  const GUARD_TARGET_H = 44;
  const s = GUARD_TARGET_H / sprite.texture.height;
  sprite.scale.set(s, s);

  sprite.position.set(guard.x, guard.y - bob);

  // Tint shifts with alert state — a subtle red push when chasing,
  // amber when investigating, neutral on patrol. Multiplicative so
  // the helmet's red lens accents stay visible at any tint.
  const isChase = guard.state === 'chase';
  const isSus = guard.state === 'suspicious' || guard.state === 'investigate';
  if (isChase) {
    sprite.tint = 0xffb0b0;
  } else if (isSus) {
    sprite.tint = 0xffd4a8;
  } else {
    sprite.tint = 0xffffff;
  }
  sprite.alpha = 1;

  // Pulsing red outline when in chase — drawn in dyn Graphics under
  // the sprite, so it reads as a glow ring around the silhouette.
  if (isChase) {
    const t = performance.now() / 220;
    const pulse = 0.5 + 0.5 * Math.sin(t);
    g.circle(guard.x, guard.y, 16 + pulse * 1.5).stroke({
      color: 0xff4a5a,
      alpha: 0.45 + 0.25 * pulse,
      width: 1.5,
    });
  }
}

// ===== KO body (sprite-driven, lying flat on the ground) =====

function drawBody(g: Graphics, guard: Guard, sprite: Sprite): void {
  const x = guard.x;
  const y = guard.y;

  // Sprite update — uniform scale targeting the longest source dim so
  // a sprawled body covers roughly the same screen footprint as a
  // standing guard's height (44 px). PNG is 258×205 → ~44 wide × 35 tall.
  const TARGET_LONG = 44;
  const longest = Math.max(sprite.texture.width, sprite.texture.height);
  const s = TARGET_LONG / longest;
  sprite.scale.set(s, s);
  sprite.position.set(x, y);
  sprite.visible = true;
  // Desaturated tint — signals "neutralised, ignore me" to the player
  // so a body next to the player doesn't read as another active threat.
  sprite.tint = 0x9099a4;
  sprite.alpha = 0.85;

  // No floor shadow here. A standing character casts a shadow at its
  // feet because the silhouette has vertical depth above the ground;
  // a sprawled body IS at ground level, and a shadow underneath it
  // looked like the body was floating above the floor.

  // Faint sleep Z above the head — kept low-alpha so it doesn't
  // compete with the active gardes for the player's attention. The
  // desaturated tint above is the primary "ignore me" cue.
  const t = (performance.now() / 800) % 1;
  const zAlpha = 0.2 + 0.2 * Math.sin(t * Math.PI * 2);
  g.circle(x + 16, y - 14, 1.6).fill({ color: 0xffffff, alpha: zAlpha });
}

// ===== Box (cardboard with tape) =====

function drawBox(g: Graphics, x: number, y: number, carried: boolean, hasBody: boolean): void {
  // Ground shadow
  g.rect(x - 13, y + 13, 28, 6).fill({ color: 0x000000, alpha: 0.55 });

  // Box body — base color varies subtly when carried (lifted)
  const lift = carried ? 1 : 0;
  const base = carried ? 0xb27940 : 0x9c6b34;
  g.rect(x - 16, y - 16 - lift, 32, 32).fill(base);
  // Inner darker face on the south/east sides for pseudo-3D
  g.rect(x - 16, y + 12 - lift, 32, 4).fill(0x6e4a22);
  g.rect(x + 12, y - 16 - lift, 4, 32).fill(0x6e4a22);
  // Outline
  g.rect(x - 16, y - 16 - lift, 32, 32).stroke({ color: 0x3e2812, width: 2 });

  // Cardboard flaps — diagonal crease lines
  g.moveTo(x - 16, y - 16 - lift)
    .lineTo(x, y - lift)
    .lineTo(x + 16, y - 16 - lift)
    .moveTo(x - 16, y + 16 - lift)
    .lineTo(x, y - lift)
    .lineTo(x + 16, y + 16 - lift)
    .stroke({ color: 0x3e2812, width: 1.5 });

  // Packing tape cross (lighter, semi-transparent)
  g.rect(x - 16, y - 3 - lift, 32, 6).fill({ color: 0xe6c993, alpha: 0.55 });
  g.rect(x - 3, y - 16 - lift, 6, 32).fill({ color: 0xe6c993, alpha: 0.55 });

  // Carried indicator — soft white outline
  if (carried) {
    g.rect(x - 17, y - 17 - lift, 34, 34).stroke({ color: 0xffffff, alpha: 0.4, width: 1 });
  }

  // Body inside — small Z indicator + red tinge through tape
  if (hasBody) {
    g.rect(x - 16, y - 3 - lift, 32, 6).fill({ color: 0xff5050, alpha: 0.18 });
    g.circle(x + 9, y - 9 - lift, 3).fill({ color: 0xff5050, alpha: 0.7 });
    g.circle(x + 9, y - 9 - lift, 1.4).fill(0xffffff);
  }
}

// ===== Particles =====

function drawParticles(g: Graphics, world: World): void {
  g.clear();
  for (const p of world.particles) {
    drawParticle(g, p);
  }
}

function drawParticle(g: Graphics, p: Particle): void {
  const t = p.life / p.maxLife;
  const size = p.size * (0.6 + 0.4 * t);
  if (p.shape === 'spark') {
    // Slim outward streak — small line oriented along velocity
    const len = Math.max(2, Math.hypot(p.vx, p.vy) * 0.06);
    const ang = Math.atan2(p.vy, p.vx);
    const x2 = p.x - Math.cos(ang) * len;
    const y2 = p.y - Math.sin(ang) * len;
    g.moveTo(p.x, p.y).lineTo(x2, y2).stroke({ color: p.color, alpha: t, width: size });
  } else {
    g.circle(p.x, p.y, size).fill({ color: p.color, alpha: t * 0.85 });
  }
}

// ===== Alerts pool =====

function drawAlerts(stage: Stage, world: World): void {
  let poolIdx = 0;
  for (const guard of world.guards) {
    if (guard.ko || guard.alert <= 12) continue;
    let entry = alertPool[poolIdx++];
    if (!entry) {
      const text = new Text({
        text: '?',
        style: {
          fontFamily: 'monospace',
          fontSize: 18,
          fontWeight: 'bold',
          fill: 0xffb84a,
        },
      });
      text.anchor.set(0.5, 1);
      stage.layers.alerts.addChild(text);
      entry = { text, ownerId: guard.id };
      alertPool.push(entry);
    }
    const isFull = guard.alert >= 100;
    entry.text.text = isFull ? '!' : '?';
    entry.text.style.fill = isFull ? 0xff4a5a : 0xffb84a;
    // Bobbing position over the head
    const bob = Math.sin(performance.now() / 200) * 1.5;
    entry.text.position.set(guard.x, guard.y - 24 - bob);
    entry.text.visible = true;
    entry.ownerId = guard.id;
  }
  for (let i = poolIdx; i < alertPool.length; i++) {
    alertPool[i]!.text.visible = false;
  }
}

// ===== Color parsing =====

function parseRgba(rgba: string): { color: number; alpha: number } {
  const m = /rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([0-9.]+)\s*)?\)/i.exec(rgba);
  if (!m) return { color: 0xffffff, alpha: 1 };
  const r = parseInt(m[1]!, 10);
  const gC = parseInt(m[2]!, 10);
  const b = parseInt(m[3]!, 10);
  const a = m[4] ? parseFloat(m[4]) : 1;
  return { color: (r << 16) | (gC << 8) | b, alpha: a };
}
